import React, { useState } from 'react';
import { Alert, Box, Button, IconButton, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { SECTIONS, UNITS, parseNumber, sectionText, unitShort } from '../labels';
import { ItemChoice, ItemPicker } from '../common';

/** "Casa · Mercearia · un · despensa · 2 receitas". */
export function catalogSubtitle(item: T.CatalogItem): string {
  const parts = [sectionText(item.storeSection), unitShort(item.defaultUnit)];
  if (item.kind === 'Household') parts.unshift('Casa');
  if (item.isPantryStaple) parts.push('despensa');
  if (item.recipeCount > 0) parts.push(item.recipeCount === 1 ? '1 receita' : `${item.recipeCount} receitas`);
  return parts.join(' · ');
}

/** Adds something to the "to buy" list by hand: an existing catalog item or a new one. */
const AddShoppingItemPage: React.FC = () => {
  const navigate = useNavigate();
  const [choice, setChoice] = useState<ItemChoice | null>(null);
  const [kind, setKind] = useState<T.CatalogItemKind>('Food');
  const [section, setSection] = useState<T.StoreSection>('Other');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState<T.MeasureUnit | ''>('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const choose = (value: ItemChoice | null) => {
    setChoice(value);
    setError(null);
    if (value && 'item' in value) setUnit(value.item.defaultUnit);
    if (value && 'newName' in value) setSection('Other');
  };

  const save = async () => {
    if (!choice) { setError('Escolhe ou cria o artigo.'); return; }
    let value: number | null = null;
    if (quantity.trim()) {
      value = parseNumber(quantity);
      if (value === null || value <= 0) { setError('A quantidade deve ser um número positivo.'); return; }
    }
    const isNew = 'newName' in choice;
    setSaving(true);
    try {
      const need = await api.addShoppingNeed({
        catalogItemPublicId: isNew ? null : choice.item.publicId,
        newItemName: isNew ? choice.newName : null,
        newItemKind: isNew ? kind : null,
        newItemSection: isNew ? section : null,
        quantity: value,
        unit: unit || null,
        note: note.trim() || null,
      });
      // The list says so, and asks "Passo a prever?" when the app did not see it coming
      navigate('/casa/compras', { state: { added: need } });
    } catch (e: any) {
      setError(e.message ?? 'Ocorreu um erro.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton aria-label="Voltar" onClick={() => navigate('/casa/compras')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>Juntar à lista</Typography>
      </Stack>
      <Stack spacing={2} sx={{ maxWidth: 520 }}>
        <ItemPicker value={choice} onChange={choose} label="Artigo (ex.: Leite, detergente)" autoFocus />

        {choice && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 600 }}>{'item' in choice ? choice.item.name : choice.newName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {'item' in choice ? catalogSubtitle(choice.item) : 'Artigo novo: fica no catálogo da casa.'}
            </Typography>
            {'newName' in choice && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
                <TextField select label="Tipo" value={kind} onChange={e => setKind(e.target.value as T.CatalogItemKind)} fullWidth>
                  <MenuItem value="Food">Alimentar</MenuItem>
                  <MenuItem value="Household">Casa</MenuItem>
                </TextField>
                <TextField select label="Secção do supermercado" value={section} onChange={e => setSection(e.target.value as T.StoreSection)} fullWidth>
                  {SECTIONS.map(s => <MenuItem key={s.value} value={s.value}>{s.text}</MenuItem>)}
                </TextField>
              </Stack>
            )}
          </Paper>
        )}

        <Stack direction="row" spacing={1.5}>
          <TextField
            label="Quantidade" placeholder="Opcional" value={quantity} onChange={e => setQuantity(e.target.value)}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }} fullWidth
          />
          <TextField select label="Unidade" value={unit} onChange={e => setUnit(e.target.value as T.MeasureUnit | '')} fullWidth>
            <MenuItem value="">—</MenuItem>
            {UNITS.map(u => <MenuItem key={u.value} value={u.value}>{u.text}</MenuItem>)}
          </TextField>
        </Stack>
        <TextField
          label="Nota" placeholder="Opcional (ex.: marca, sem lactose)" value={note} onChange={e => setNote(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />

        {error && <Alert severity="error">{error}</Alert>}
        <Box>
          <Button variant="contained" onClick={save} disabled={saving}>Juntar</Button>
        </Box>
      </Stack>
    </>
  );
};

export default AddShoppingItemPage;
